import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { engagementService, clientService, proposalService } from '../services/api';
import RichTextEditor from '../components/common/RichTextEditor';

export default function EngagementEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams.get('client');
  const fromProposalId = searchParams.get('proposal');
  const preselectedStartDate = searchParams.get('startDate');
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [clients, setClients] = useState([]);
  const [proposals, setProposals] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'active',
    client: preselectedClientId || '',
    proposal: fromProposalId || '',
    startDate: preselectedStartDate || '',
    endDate: '',
    rate: '',
    rateType: 'hourly',
    budget: '',
    notes: '',
  });

  useEffect(() => {
    loadInitialData();
  }, [id]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load clients for dropdown
      const clientsData = await clientService.getAll();
      setClients(clientsData);

      // Load proposals for dropdown - show pending and accepted proposals
      const proposalsData = await proposalService.getAll();
      setProposals(proposalsData.filter(p => 
        p.status === 'accepted' || 
        p.status === 'pending' || 
        p.id === parseInt(fromProposalId)
      ));

      // If editing, load the engagement
      if (isEditing) {
        const engagement = await engagementService.getById(id);
        setFormData({
          title: engagement.title || '',
          description: engagement.description || '',
          status: engagement.status || 'active',
          client: engagement.client?.id || engagement.client || '',
          proposal: engagement.proposal?.id || engagement.proposal || '',
          startDate: engagement.startDate ? engagement.startDate.split('T')[0] : '',
          endDate: engagement.endDate ? engagement.endDate.split('T')[0] : '',
          rate: engagement.rate || '',
          rateType: engagement.rateType || 'hourly',
          budget: engagement.budget || '',
          notes: engagement.notes || '',
        });
      } else if (fromProposalId) {
        // Pre-populate from proposal
        const proposal = proposalsData.find(p => p.id === parseInt(fromProposalId));
        if (proposal) {
          setFormData(prev => ({
            ...prev,
            title: proposal.title || '',
            description: proposal.description || '',
            client: proposal.client?.id || proposal.client || preselectedClientId || '',
            proposal: fromProposalId,
            budget: proposal.amount || '',
          }));
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage({ type: 'error', text: 'Error loading data.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // When client changes, filter proposals for that client
    if (name === 'client') {
      setFormData(prev => ({ ...prev, [name]: value, proposal: '' }));
    }
  };

  const handleDescriptionChange = (content) => {
    setFormData({ ...formData, description: content });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    if (!formData.client) {
      setMessage({ type: 'error', text: 'Please select a client.' });
      setSaving(false);
      return;
    }

    try {
      const submitData = {
        title: formData.title,
        description: formData.description,
        status: formData.status,
        client: parseInt(formData.client),
        rateType: formData.rateType,
        rate: formData.rate ? parseFloat(formData.rate) : 0,
        budget: formData.budget ? parseFloat(formData.budget) : 0,
        notes: formData.notes,
      };

      // Only include optional fields if they have values
      if (formData.proposal) {
        submitData.proposal = parseInt(formData.proposal);
      }
      if (formData.startDate) {
        submitData.startDate = formData.startDate;
      }
      if (formData.endDate) {
        submitData.endDate = formData.endDate;
      }

      if (isEditing) {
        await engagementService.update(id, submitData);
        setMessage({ type: 'success', text: 'Engagement updated successfully!' });
      } else {
        const newEngagement = await engagementService.create(submitData);
        setMessage({ type: 'success', text: 'Engagement created successfully!' });
        setTimeout(() => navigate(`/engagements/${newEngagement.id}`), 1000);
      }
    } catch (error) {
      console.error('Error saving engagement:', error);
      setMessage({ type: 'error', text: 'Error saving engagement. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'badge-success',
      paused: 'badge-warning',
      completed: 'badge-info',
      cancelled: 'badge-danger',
    };
    return badges[status] || 'badge-gray';
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Filter proposals by selected client
  const filteredProposals = formData.client
    ? proposals.filter(p => (p.client?.id || p.client) === parseInt(formData.client))
    : proposals;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{isEditing ? 'Edit Engagement' : 'New Engagement'} | CR Hood Admin</title>
      </Helmet>

      <div className="max-w-4xl">
        <div className="mb-6">
          <Link to="/engagements" className="text-accent hover:underline text-sm flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Engagements
          </Link>
          <div className="flex items-center gap-3 mt-2">
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Engagement' : 'Create New Engagement'}
            </h1>
            {isEditing && formData.status && (
              <span className={`badge ${getStatusBadge(formData.status)}`}>
                {formData.status}
              </span>
            )}
          </div>
        </div>

        {message.text && (
          <div
            className={`p-4 rounded-lg mb-6 ${
              message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}
          >
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Client *</label>
                  <select
                    name="client"
                    value={formData.client}
                    onChange={handleChange}
                    required
                    className="form-input"
                  >
                    <option value="">Select a client...</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.company ? `${client.company} (${client.name})` : client.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">From Proposal</label>
                  <select
                    name="proposal"
                    value={formData.proposal}
                    onChange={handleChange}
                    className="form-input"
                    disabled={!formData.client}
                  >
                    <option value="">None (standalone engagement)</option>
                    {filteredProposals.map((proposal) => (
                      <option key={proposal.id} value={proposal.id}>
                        {proposal.title} (${proposal.amount?.toLocaleString() || '0'})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="form-label">Engagement Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="e.g., NetSuite Implementation - Phase 1"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Rate Type</label>
                  <select
                    name="rateType"
                    value={formData.rateType}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="hourly">Hourly</option>
                    <option value="fixed">Fixed Price</option>
                    <option value="retainer">Retainer</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Billing */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Billing</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">
                  {formData.rateType === 'hourly' ? 'Hourly Rate ($)' : 
                   formData.rateType === 'retainer' ? 'Monthly Retainer ($)' : 'Rate ($)'}
                </label>
                <input
                  type="number"
                  name="rate"
                  value={formData.rate}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="form-input"
                  placeholder="0.00"
                />
                {formData.rate && (
                  <p className="text-sm text-gray-500 mt-1">
                    ${formatCurrency(formData.rate)}
                    {formData.rateType === 'hourly' && '/hr'}
                    {formData.rateType === 'retainer' && '/month'}
                  </p>
                )}
              </div>
              <div>
                <label className="form-label">Budget / Contract Value ($)</label>
                <input
                  type="number"
                  name="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="form-input"
                  placeholder="0.00"
                />
                {formData.budget && (
                  <p className="text-sm text-gray-500 mt-1">
                    ${formatCurrency(formData.budget)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Scope of Work</h2>
            <div>
              <label className="form-label">Description</label>
              <RichTextEditor
                value={formData.description}
                onChange={handleDescriptionChange}
                placeholder="Describe the engagement scope, deliverables, and any other details..."
                minHeight="250px"
              />
            </div>
          </div>

          {/* Internal Notes */}
          <div className="card">
            <h2 className="text-lg font-semibold mb-4">Internal Notes</h2>
            <div>
              <label className="form-label">Notes (not visible to client)</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
                className="form-input"
                placeholder="Internal notes about this engagement..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {isEditing ? 'Changes will be saved to the existing engagement.' : '* Required fields'}
            </div>
            <div className="flex gap-3">
              <Link to="/engagements" className="btn btn-secondary">
                Cancel
              </Link>
              <button
                type="submit"
                disabled={saving}
                className="btn btn-primary"
              >
                {saving ? 'Saving...' : isEditing ? 'Update Engagement' : 'Create Engagement'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
